import React from 'react';
import { RangeSlider } from './RangeSlider';
import { Assumptions, CompModel } from '../types/compensation';
import { formatDkk } from '../utils/format';
interface LeverPanelProps {
  model: CompModel;
  assumptions: Assumptions;
  onFixedChange: (value: number) => void;
  onShareChange: (value: number) => void;
}
export function LeverPanel({
  model,
  assumptions,
  onFixedChange,
  onShareChange
}: LeverPanelProps) {
  const commissionPct = Math.round(model.commissionShare * 100);
  const equityPct = 100 - commissionPct;
  return <section aria-label="Compensation levers" className="rounded-card border border-line bg-ink-900 p-6 lg:p-8" data-id="element-91" data-mp="a4rspmfswhmw">
      <div data-id="element-92" data-mp="ow0ipwnm850k">
        <div className="flex items-baseline justify-between gap-4" data-id="element-93" data-mp="jvcrb9vh5m5t">
          <label htmlFor="fixed-salary" className="text-[11px] font-medium uppercase tracking-[0.18em] text-security" data-id="element-94" data-mp="jx7c8o6qxe9w">
            1 · Fixed monthly salary
          </label>
          <span className="tnum text-lg font-semibold text-paper" data-id="element-95" data-mp="sty4epi6g8z7">
            {formatDkk(model.fixedMonthly)}
          </span>
        </div>
        <div className="mt-4" data-id="element-96" data-mp="325d3ao0n7rgs">
          <RangeSlider id="fixed-salary" label="Fixed monthly salary" min={assumptions.minFixedSalary} max={assumptions.maxFixedSalary} step={1000} value={model.fixedMonthly} accent="#7FA3C4" onChange={onFixedChange} ariaValueText={`${formatDkk(model.fixedMonthly)} per month`} data-id="element-97" data-mp="326oeva0by3ig" />
        </div>
        <div className="tnum flex justify-between text-xs text-paper-dim" data-id="element-98" data-mp="3l37gle8t0p4z">
          <span data-id="element-99" data-mp="3px1z4ecoay2h">{formatDkk(assumptions.minFixedSalary)}</span>
          <span data-id="element-100" data-mp="3uuk8u6yilhbj">{formatDkk(assumptions.maxFixedSalary)}</span>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-paper-muted" data-id="element-101" data-mp="3uwobymolacar">
          Salary sacrifice against the {formatDkk(assumptions.referenceMonthlySalary)}{' '}
          market reference is{' '}
          <span className="tnum text-paper" data-id="element-102" data-mp="24qicotakngqd">
            {formatDkk(model.monthlySacrifice)}/month
          </span>{' '}
          — an annual pool of{' '}
          <span className="tnum text-paper" data-id="element-103" data-mp="1zti86mtdem1d">
            {formatDkk(model.annualSacrifice)}
          </span>{' '}
          to allocate below.
        </p>
      </div>

      <div className="mt-10 border-t border-line-soft pt-8" data-id="element-104" data-mp="28rap1veqq1qv">
        <div className="flex items-baseline justify-between gap-4" data-id="element-105" data-mp="2nh9jpo736v1l">
          <label htmlFor="allocation" className="text-[11px] font-medium uppercase tracking-[0.18em] text-paper-dim" data-id="element-106" data-mp="2nff0cebgrazm">
            2 · Allocate the pool
          </label>
          <span className="tnum text-sm text-paper-muted" data-id="element-107" data-mp="2wftwy9tvocif">
            One krone, one place
          </span>
        </div>

        <div className="mt-4 flex items-end justify-between gap-4" data-id="element-108" data-mp="123x3ycfseyl9">
          <div data-id="element-109" data-mp="1246sdvl6oxou">
            <p className="tnum text-2xl font-semibold text-performance" data-id="element-110" data-mp="125q8nm6ub8qm">
              {commissionPct}%
            </p>
            <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-paper-dim" data-id="element-111" data-mp="nyn06pov82ok">
              Commission
            </p>
          </div>
          <div className="text-right" data-id="element-112" data-mp="ryotaagjbns8">
            <p className="tnum text-2xl font-semibold text-ownership" data-id="element-113" data-mp="s1t5nv45lptz">
              {equityPct}%
            </p>
            <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-paper-dim" data-id="element-114" data-mp="j7eqrtnjchbe">
              Equity
            </p>
          </div>
        </div>

        <div className="mt-3" data-id="element-115" data-mp="48uh7v41i391">
          <RangeSlider id="allocation" label="Commission versus equity allocation" min={0} max={100} step={1} value={commissionPct} accent="#E2A03F" onChange={v => onShareChange(v / 100)} ariaValueText={`${commissionPct} percent commission, ${equityPct} percent equity`} data-id="element-116" data-mp="99h2551rkii9" />
        </div>

        <div className="tnum grid grid-cols-2 gap-4 border-t border-line-soft pt-4 text-sm" data-id="element-117" data-mp="1umto4yjnpb71">
          <div data-id="element-118" data-mp="3qpya3ynzyz9o">
            <p className="text-paper-dim" data-id="element-119" data-mp="3qqhtzkx7jxqc">To commission</p>
            <p className="mt-1 text-paper" data-id="element-120" data-mp="3qnvn7ni55m96">{formatDkk(model.commissionCash)}</p>
          </div>
          <div className="text-right" data-id="element-121" data-mp="3lroclhesvmkw">
            <p className="text-paper-dim" data-id="element-122" data-mp="3grim44k9o64e">To equity</p>
            <p className="mt-1 text-paper" data-id="element-123" data-mp="3gpz3i7b51izg">{formatDkk(model.equityCash)}</p>
          </div>
        </div>
      </div>
    </section>;
}